'use client';

import { useEffect } from 'react';
import dynamic from 'next/dynamic';
import type { ReactNode } from 'react';
import { RpErrorBoundary, useBootstrap } from '@zg-rentals/rp-bootstrap-browser';
import { useRegisterNotification } from '@zg-rentals/register-notification';
import { getQueryClient, QueryProvider } from '@zg-rentals/react-query5/client';
import { type DehydratedState, HydrationBoundary } from '@zg-rentals/react-query5';

import { initializeHotpadsAnalytics, trackComscoreEvent } from '@/src/analytics';
import { ThemeProvider } from '@/src/providers/theme-provider';
import { ToastProvider } from '@zillow/constellation-10';
import { FeatureFlagsProvider, type FlagValues } from '@/src/lib/feature-flags/FeatureFlagsProvider';
import { processFeatureFlagsFromURL } from '@/src/lib/feature-flags/queryParams';
import { processExperimentParamsFromURL } from '@/src/lib/experiments';
import type { InitialUserData } from '@/src/lib/api/endpoints/user';
import { InitialUserProvider } from '@/src/lib/auth/InitialUserContext';
import { SavedListingsProvider } from '@/src/components/features/property/contexts/SavedListingsContext';
import { ChunkLoadRecoveryBoundary, clearChunkReloadGuard } from './_components/ChunkLoadRecoveryBoundary';
import { useHotpadsComscorePageTracking } from '@/src/lib/comscore/useHotpadsComscorePageTracking';
import { usePisPageCount } from '@/src/lib/ads/usePisPageCount';
import { configureSatelliteClient } from '@/src/lib/api/client/satelliteClient';
import { configureHotpadsApiV3Client } from '@/src/lib/api/client/hotpadsApiV3Client';
import { GoogleMapsLoaderHost } from '@/src/lib/hooks/map/GoogleMapsLoaderHost';
import { queryClientOptions } from '@/src/queryClientOptions';
import { emitTrackGoogleLoginEvent } from '@/src/analytics/hotpads/events';
import { consumeGoogleCredentialMarker } from '@/src/lib/auth/google-credential-tracking';
import { useTrackThemeAnalytics } from '@/src/lib/hooks/ui/useTrackThemeAnalytics';

configureSatelliteClient();
configureHotpadsApiV3Client();

const WebMcpSearchTool = dynamic(() => import('./_components/WebMcpSearchTool').then((m) => m.WebMcpSearchTool), {
  ssr: false,
});

export function AppBootstrap({ children }: { children: ReactNode }) {
  useBootstrap(() => import('../bootstrap/browser'));
  useHotpadsComscorePageTracking(() => trackComscoreEvent({}));
  usePisPageCount();
  useRegisterNotification({ ref: 'HOTPADS_NEXT' });
  useTrackThemeAnalytics();

  useEffect(() => {
    // Keep local dev quiet: clickstream's trace-browser module patches window.fetch
    // and tries to reach internal corp endpoints that are not reachable locally.
    if (process.env.NODE_ENV === 'development') return;
    initializeHotpadsAnalytics();
  }, []);

  useEffect(() => {
    // Reaching this effect means the app rendered without a chunk failure, so
    // release the one-shot reload guard — a chunk error later in this session
    // (e.g. on a subsequent navigation) should get its own recovery attempt.
    clearChunkReloadGuard();
    processFeatureFlagsFromURL();
    processExperimentParamsFromURL();
    if (process.env.NODE_ENV !== 'development' && consumeGoogleCredentialMarker()) {
      emitTrackGoogleLoginEvent();
    }
  }, []);

  return <>{children}</>;
}

export type ProvidersProps = {
  children: ReactNode;
  flags: FlagValues;
  initialUser?: InitialUserData | null;
  queryState?: DehydratedState;
};

export function Providers({ children, flags, initialUser, queryState }: ProvidersProps) {
  return (
    <InitialUserProvider initialUser={initialUser}>
      <QueryProvider client={getQueryClient(queryClientOptions)}>
        <HydrationBoundary state={queryState}>
          <RpErrorBoundary>
            <ChunkLoadRecoveryBoundary>
              <FeatureFlagsProvider initialFlags={flags}>
                <ThemeProvider attribute="class" defaultTheme="system" enableSystem storageKey="hp-theme-system">
                  <ToastProvider placement="top-end">
                    {/*
                     * SavedListingsProvider is global, not scoped to the search shell:
                     * <ListingCard> renders on the homepage (FeaturedRentals, BudgetExplorer)
                     * and other non-resource routes, and its save button calls
                     * useSavedListings(). Scoping the provider to resource routes only
                     * crashes those pages with "useSavedListings must be used within a
                     * SavedListingsProvider".
                     */}
                    <SavedListingsProvider>
                      <GoogleMapsLoaderHost />
                      <WebMcpSearchTool />
                      <AppBootstrap>{children}</AppBootstrap>
                    </SavedListingsProvider>
                  </ToastProvider>
                </ThemeProvider>
              </FeatureFlagsProvider>
            </ChunkLoadRecoveryBoundary>
          </RpErrorBoundary>
        </HydrationBoundary>
      </QueryProvider>
    </InitialUserProvider>
  );
}
